import React, { FC, useEffect, useMemo, useState } from "react"
import "antd/dist/antd.less"
import { MainLayout } from "../layouts"
import { HashRouter, Router as HistoryRouter, Switch, Route, Redirect, useHistory } from "react-router-dom"
import type { History } from "history"
import CourseDetails from "../containers/CourseDetails/CourseDetails"
import CourseTableContainer from "../containers/CoursesTable/CourseTableContainer"
import { CourseFilterProvider } from "../containers/CoursesTable/CourseFilterContext"
import { InCommingAttributes } from "../types/filters"
import { CourseListFilters, DayFilter, GetCompanyQuery, useGetCompanyQuery, useSettingEndUserAppQuery } from "../generated/graphql"
import { getEnumKeyByEnumValue } from "../helpers/enumHelpers"
import { applyTenantDefault, normalizeLanguage } from "../helpers/resolveLanguage"
import {
    URL_ACCOUNT,
    URL_AGB,
    URL_BASE,
    URL_BOOKING_SUMMARY,
    URL_COURSE_DETAILS,
    URL_DATENSCHUTZ,
    URL_FORGOT_PASSWORD,
    URL_INTEREST_DETAILS,
    URL_INTEREST_REGISTRATION,
    URL_LOGIN,
    URL_LOGIN_COMPLETE_PASSWORDLESS,
    URL_LOGIN_COMPLETE_PASSWORDLESS_EMAIL,
    URL_LOGIN_EMAIL,
    URL_PAYMENT_RETURN,
    URL_REGISTER,
    URL_ACTIVATE_ACCOUNT,
    URL_REGISTER_SEPA_EDIT,
    URL_REGISTER_SEPA_EDIT_ID,
    URL_RESULTS,
    URL_WIDERRUF,
} from "../models/url"
import LoginPanel from "../containers/login/LoginPanel"
import RegistrationPanel from "../containers/login/RegistrationPanel"
import ActivateAccountPanel from "../containers/login/ActivateAccountPanel"
import AccountDashboard from "../containers/account/AccountDashboard"
import BookingSummary from "../containers/CreateBooking/BookingSummary/BookingSummary"
import ForgotPasswordPanel from "../containers/login/ForgotPasswordPanel"
import ResultScreen from "../views/ResultScreens/ResultScreen"
import CompletePasswordLessFlowPanel from "../containers/login/CompletePasswordLessFlowPanel"
import { AppSettings } from "../layouts/Main/MainLayout"
import { Alert, Space } from "antd"
import { handlePrerequisiteCourseId, handleRestrictiveCourses } from "./handleParameters"
import { parseListType } from "../containers/interest/listType"
import InterestDetails from "../containers/interest/InterestDetails"
import InterestRegistration from "../containers/interest/InterestRegistration"
import SepaDataEdit from "../containers/login/SepaDataEdit"
import AGB from "../views/AGB/AGB"
import Datenschutz from "../views/Datenschutz/Datenschutz"
import Widerruf from "../views/Widerruf/Widerruf"
import { PaymentReturn } from "../containers/PaymentReturn/PaymentReturn"
import i18n from "../i18n"
import { useTranslation } from "react-i18next"
import { ConfigProvider } from "antd"
import deDE from "antd/lib/locale/de_DE"
import enUS from "antd/lib/locale/en_US"
import moment from "moment-timezone"
import { useMaintenance } from "../contexts/MaintenanceContext"
import ErrorBoundary from "../components/ErrorBoundary"
import { setSentryUser, clearSentryUser } from "../helpers/sentry"
import { initLogRocket, resetLogRocketSession } from "../helpers/logrocket"
import { safeStorage } from "../helpers/safeStorage"
import { decodeDiscountCodeParam } from "../containers/CreateBooking/BookingSummary/BookingPricePreview/discountCodeMessages"
import { rememberLinkDiscountCode } from "../containers/CreateBooking/BookingSummary/BookingPricePreview/linkDiscountCode"
import { rememberUtmSource } from "../containers/CreateBooking/BookingSummary/utmSource"

// Bereits gemeldete ungültige Parameter-/Settings-Werte — verhindert Warn-Spam,
// da die Parameter-Verarbeitung bei jedem Render der App erneut durchläuft.
const warnedInvalidValues = new Set<string>()

const warnOnce = (key: string, message: string) => {
    if (warnedInvalidValues.has(key)) return
    warnedInvalidValues.add(key)
    console.warn(message)
}

export type ParentParams = {
    code?: string
    internalPath?: string
    courseId?: string
    discountCode?: string
    utmSource?: string
}

type AppProps = {
    parameters?: InCommingAttributes
    version: string
    parentUrlParams?: ParentParams
    // Embed-Modus (sc-3381): eigene History statt HashRouter, damit die Kundenseiten-URL keinen Hash bekommt
    history?: History
    // Embed-Modus mit mehreren Modulen je Seite: CSS-Variablen (Maximalbreite) am eigenen Wurzelelement statt am <html>
    styleRoot?: HTMLElement
}

// // Add this mapping near the top of the file, after imports
// const navigationMap: Record<string, string> = {
//     login: URL_LOGIN,
//     register: URL_REGISTER,
//     account: URL_ACCOUNT,
//     forgotPassword: URL_FORGOT_PASSWORD,
// }

// Auf Modulebene, damit der Router-Typ über Renders stabil bleibt (sonst Remount der ganzen App)
const AppRouter: FC<{ history?: History; children?: React.ReactNode }> = ({ history, children }) => (history ? <HistoryRouter history={history}>{children}</HistoryRouter> : <HashRouter>{children}</HashRouter>)

const App: FC<AppProps> = ({ parameters, version, parentUrlParams, history: embedHistory, styleRoot }) => {
    const [isLoggedIn, setIsLoggedIn] = useState(!!safeStorage.getItem("loggedInUser"))
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [accessToken, setAccesToken] = useState<string | undefined>()
    const updatedIncommingFilters: CourseListFilters = {}
    const otherSettings: Partial<InCommingAttributes> = {}
    let parentUrl: string = window.location.href
    const [appSettings, setAppSettings] = useState<AppSettings | undefined>()
    const [companySettings, setCompanySettings] = useState<GetCompanyQuery["companyPublic"]>()
    const [parentParams, setParentParams] = useState<ParentParams>()
    const atLeastOneParentUrlParam = Object.values(parentParams ?? {}).some((value) => value !== undefined)
    const [maxWidth, setMaxWidth] = useState<string>("1200px")

    // Maintenance-Status (über public endpoint + Error-Fallback)
    const { maintenanceInfo, maintenanceActive } = useMaintenance()

    const maintenanceUntilText = useMemo(() => {
        if (!maintenanceInfo?.untilIso) return undefined
        const parsed = new Date(maintenanceInfo.untilIso)
        if (Number.isNaN(parsed.getTime())) return undefined
        return parsed.toLocaleString("de-DE", {
            dateStyle: "medium",
            timeStyle: "short",
        })
    }, [maintenanceInfo?.untilIso])

    // LogRocket nur für Mandanten auf der Hostname-Allowlist (Issue #93), fail-closed.
    useEffect(() => {
        if (isLoggedIn) {
            void initLogRocket()
        } else {
            void resetLogRocketSession()
        }
    }, [isLoggedIn])

    useEffect(() => {
        setParentParams(parentUrlParams)
        // Rabattcode aus dem Link bis zur Buchungsübersicht mitnehmen (auch über den Login-Umweg).
        if (parentUrlParams?.discountCode) {
            const discountCodeParam = decodeDiscountCodeParam(parentUrlParams.discountCode)

            rememberLinkDiscountCode(discountCodeParam)
        }
        // Herkunft (z. B. Buchen-Link der öffentlichen Kursseite) bis zur Buchung mitnehmen.
        if (parentUrlParams?.utmSource !== undefined) {
            rememberUtmSource(parentUrlParams.utmSource)
        }
    }, [parentUrlParams])

    // Verarbeite maxWidth Parameter
    useEffect(() => {
        // Lese Parameter direkt aus URL (unterstützt Hash-Routing)
        const getMaxWidthFromUrl = () => {
            let searchParams = new URLSearchParams(window.location.search)

            // Falls keine Parameter in search, prüfe hash (für Hash-Routing wie /#/?param=value)
            if (!window.location.search && window.location.hash.includes("?")) {
                const hashParts = window.location.hash.split("?")
                searchParams = new URLSearchParams(hashParts[1] || "")
            }

            return searchParams.get("maxWidth") || searchParams.get("maxwidth")
        }

        const updateMaxWidth = () => {
            const maxWidthParam = getMaxWidthFromUrl()
            if (maxWidthParam) {
                // Stelle sicher, dass der Wert eine Einheit hat (px, %, em, etc.)
                const hasUnit = /\d+(px|%|em|rem|vh|vw)$/i.test(maxWidthParam)
                const finalMaxWidth = hasUnit ? maxWidthParam : `${maxWidthParam}px`
                setMaxWidth(finalMaxWidth)
                console.log("[MaxWidth] Maximale Breite gesetzt auf:", finalMaxWidth)
            } else if (parameters) {
                // Fallback: Verwende parameters Prop (für Kompatibilität)
                const maxWidthParamFromProps = parameters.maxWidth || parameters.maxwidth
                if (maxWidthParamFromProps) {
                    const hasUnit = /\d+(px|%|em|rem|vh|vw)$/i.test(maxWidthParamFromProps)
                    const finalMaxWidth = hasUnit ? maxWidthParamFromProps : `${maxWidthParamFromProps}px`
                    setMaxWidth(finalMaxWidth)
                    console.log("[MaxWidth] Maximale Breite gesetzt auf (from props):", finalMaxWidth)
                }
            }
        }

        // Initiale Verarbeitung
        updateMaxWidth()

        // Lausche auf Hash-Änderungen
        window.addEventListener("hashchange", updateMaxWidth)

        return () => {
            window.removeEventListener("hashchange", updateMaxWidth)
        }
    }, [parameters])

    // Wende maxWidth als CSS-Variable an
    useEffect(() => {
        ;(styleRoot || document.documentElement).style.setProperty("--max-content-width", maxWidth)
    }, [maxWidth, styleRoot])

    // CSS-Injection für benutzerdefiniertes CSS
    useEffect(() => {
        const loadCustomCss = async () => {
            console.log("[CSS Injection] Starting CSS injection...")
            if (!parameters) {
                console.log("[CSS Injection] No parameters provided")
                return
            }

            console.log("[CSS Injection] Parameters:", parameters)
            const customCssUrl = parameters.customCssUrl || parameters.customcssurl || parameters.cssUrl || parameters.cssurl
            const customCss = parameters.customCss || parameters.customcss

            console.log("[CSS Injection] customCssUrl:", customCssUrl)
            console.log("[CSS Injection] customCss:", customCss)

            // Warte bis das Dokument vollständig geladen ist
            const ensureDocumentReady = () => {
                return new Promise<void>((resolve) => {
                    if (document.readyState === "complete" || document.readyState === "interactive") {
                        // Warte kurz, um sicherzustellen, dass andere Stylesheets geladen sind
                        setTimeout(resolve, 100)
                    } else {
                        window.addEventListener("load", () => setTimeout(resolve, 100))
                    }
                })
            }

            await ensureDocumentReady()

            // Entferne bereits vorhandene Custom-CSS-Elemente
            const existingLink = document.getElementById("kursorganizer-custom-css-link")
            const existingStyle = document.getElementById("kursorganizer-custom-css-style")
            if (existingLink) existingLink.remove()
            if (existingStyle) existingStyle.remove()

            // CSS-URL hat Priorität über inline CSS
            if (customCssUrl) {
                try {
                    // URL-dekodieren falls noch kodiert (zusätzliche Sicherheit)
                    let cssUrl = customCssUrl.trim()

                    console.log("[CSS Injection] Original URL:", cssUrl)

                    // Mehrfache Dekodierung für den Fall, dass die URL mehrfach kodiert wurde
                    let previousUrl = ""
                    while (previousUrl !== cssUrl && cssUrl.includes("%")) {
                        previousUrl = cssUrl
                        try {
                            cssUrl = decodeURIComponent(cssUrl)
                        } catch (decodeError) {
                            // Falls Dekodierung fehlschlägt, verwende den aktuellen Wert
                            break
                        }
                    }

                    console.log("[CSS Injection] Decoded URL:", cssUrl)

                    // Stelle sicher, dass die URL absolut ist (beginnt mit http:// oder https://)
                    if (!cssUrl.match(/^https?:\/\//i)) {
                        console.warn("[CSS Injection] Custom CSS URL is not absolute, skipping:", cssUrl)
                        return
                    }

                    // Prüfe auf Mixed Content (HTTPS-Seite lädt HTTP-Ressource)
                    const pageIsHttps = window.location.protocol === "https:"
                    const cssIsHttp = cssUrl.startsWith("http://")
                    const isMixedContent = pageIsHttps && cssIsHttp

                    if (isMixedContent) {
                        console.warn("[CSS Injection] Mixed Content erkannt - CSS wird NICHT geladen (Browser-Security)")
                        console.warn("[CSS Injection] Lösung: Verwenden Sie HTTPS für die CSS-Datei oder laden Sie sie über den WordPress-Server")
                        console.warn("[CSS Injection] Für lokale Entwicklung: Verwenden Sie ein selbst-signiertes SSL-Zertifikat")
                        // Mixed Content kann nicht umgangen werden - Browser blockiert HTTP-Ressourcen auf HTTPS-Seiten
                        return
                    }

                    const link = document.createElement("link")
                    link.id = "kursorganizer-custom-css-link"
                    link.rel = "stylesheet"
                    link.type = "text/css"
                    link.href = cssUrl
                    link.onload = () => {
                        console.log("[CSS Injection] ✓ Custom CSS loaded successfully from URL:", cssUrl)
                    }
                    link.onerror = (event) => {
                        console.error("[CSS Injection] ✗ Failed to load custom CSS from URL:", cssUrl, event)
                    }
                    // Füge das Stylesheet am Ende des head ein, damit es höhere Priorität hat
                    document.head.appendChild(link)
                    console.log("[CSS Injection] <link> element added to <head>")
                } catch (error) {
                    console.error("[CSS Injection] Error loading custom CSS URL:", error)
                }
            } else if (customCss) {
                try {
                    // URL-dekodieren falls nötig, dann Base64 dekodieren
                    let cssToDecode = customCss.trim()
                    // Prüfe ob der String URL-kodiert ist (enthält %)
                    if (cssToDecode.includes("%")) {
                        cssToDecode = decodeURIComponent(cssToDecode)
                    }
                    // Base64 dekodieren
                    const decodedCss = atob(cssToDecode)
                    const style = document.createElement("style")
                    style.id = "kursorganizer-custom-css-style"
                    style.type = "text/css"
                    style.textContent = decodedCss
                    // Füge das Stylesheet am Ende des head ein, damit es höhere Priorität hat
                    document.head.appendChild(style)
                    console.log("Custom CSS injected successfully (inline)")
                } catch (error) {
                    console.error("Error decoding custom CSS:", error)
                    // Fallback: Versuche direkt als Text zu verwenden (falls nicht base64)
                    try {
                        const style = document.createElement("style")
                        style.id = "kursorganizer-custom-css-style"
                        style.type = "text/css"
                        style.textContent = customCss.trim()
                        document.head.appendChild(style)
                        console.log("Custom CSS injected successfully (fallback, non-base64)")
                    } catch (fallbackError) {
                        console.error("Error adding custom CSS as fallback:", fallbackError)
                    }
                }
            }
        }

        loadCustomCss()
    }, [parameters])

    // Überwache Login-Status Änderungen + Sentry User-Kontext
    useEffect(() => {
        const checkLoginStatus = () => {
            const loggedInUser = safeStorage.getItem("loggedInUser")
            setIsLoggedIn(!!loggedInUser)

            if (loggedInUser) {
                setSentryUser(loggedInUser)
            } else {
                clearSentryUser()
            }
        }

        // Initial check
        checkLoginStatus()

        // Event Listener für localStorage-Änderungen (falls andere Tabs das ändern)
        window.addEventListener("storage", checkLoginStatus)

        // Custom Event Listener für localStorage-Änderungen im gleichen Tab
        window.addEventListener("localStorageChange", checkLoginStatus)

        return () => {
            window.removeEventListener("storage", checkLoginStatus)
            window.removeEventListener("localStorageChange", checkLoginStatus)
        }
    }, [])

    const { code, internalPath, courseId } = parentParams ?? {}

    const { data: appSettingsData } = useSettingEndUserAppQuery()
    useEffect(() => {
        if (appSettingsData?.settingEndUserAppPublic) {
            setAppSettings(appSettingsData.settingEndUserAppPublic)
        }
    }, [appSettingsData])

    // Rerender bei Sprachwechsel, damit auch die antd-Texte (Datepicker, leere Listen) der Sprache folgen
    useTranslation()
    const antdLocale = i18n.language.startsWith("de") ? deDE : enUS

    // Mandanten-Default für die Sprache anwenden, sobald die Einstellungen geladen sind
    useEffect(() => {
        const serverI18nRaw = (appSettingsData as any)?.settingEndUserAppPublic?.i18n as string | undefined
        if (!serverI18nRaw) return
        const serverI18n = normalizeLanguage(serverI18nRaw)
        if (!serverI18n) {
            warnOnce(
                `i18n:${serverI18nRaw}`,
                `[KursOrganizer] Nicht unterstützter i18n-Wert "${serverI18nRaw}" — unterstützt werden: de, de-DU, de-SIE, en. Die Sprache bleibt unverändert.`
            )
            return
        }

        // Ein expliziter URL-Parameter hat laut öffentlicher iFrame-Schnittstelle
        // Vorrang vor dem Mandanten-Default; sonst gilt der Mandanten-Default,
        // egal wie der Browser eingestellt ist.
        // Embed-Modus: der explizite Wert kommt aus data-i18n (parameters), die Kundenseiten-URL hat kein ?i18n
        let queryParam: string | undefined = (parameters?.i18n as string | undefined) || undefined
        try {
            queryParam = queryParam || new URLSearchParams(window.location.search).get("i18n") || undefined
        } catch {
            /* queryParam bleibt */
        }
        const target = applyTenantDefault({ queryParam, tenantDefault: serverI18n })
        if (i18n.language !== target) {
            // moment zuerst, damit der Rerender durch changeLanguage schon die neue Locale sieht
            moment.locale(target.startsWith("de") ? "de" : "en-gb")
            i18n.changeLanguage(target)
            safeStorage.setItem("i18n", target)
        }
    }, [appSettingsData, parameters])

    const { data: companySettingsData } = useGetCompanyQuery()
    useEffect(() => {
        if (companySettingsData?.companyPublic) {
            setCompanySettings(companySettingsData.companyPublic)
        }
    }, [companySettingsData])

    if (parameters) {
        Object.entries(parameters).forEach(([key, value]) => {
            // console.log("typeof key", key, ":", typeof key)
            if (typeof value === "string") {
                if (key === "city") {
                    // console.log("city")
                    updatedIncommingFilters.city = value
                } else if (key === "courseTypeId" || key === "coursetypeid") {
                    // console.log("courseTypeId::: ", value)
                    updatedIncommingFilters.courseTypeId = value
                } else if (key === "courseTypeIds" || key === "coursetypeids") {
                    // remove spaces and "%" from value
                    value = value.replace(/%20/g, "")
                    // remove spaces from value
                    value = value.replace(/ /g, "")
                    const courseTypeIds = value.split(",")
                    // console.log("courseTypeIds::: ", courseTypeIds)
                    if (courseTypeIds.length > 0 && courseTypeIds[0] !== "") {
                        updatedIncommingFilters.courseTypeIds = courseTypeIds
                        // updatedIncommingFilters.courseTypeId = value
                    }
                } else if (key === "locationId") {
                    // console.log("locationId::: ", value)
                    updatedIncommingFilters.locationId = value
                } else if (key === "instructorId") {
                    // console.log("instructorId::: ", value)
                    updatedIncommingFilters.instructorId = value
                } else if (key === "courseCategoryId" || key === "coursecategoryid") {
                    // console.log("courseCategory::: ", value)
                    updatedIncommingFilters.courseCategoryId = value
                } else if (key === "courseCategoryIds" || key === "coursecategoryids") {
                    // console.log("courseCategoryIds::: ", value)
                    value = value.replace(/%20/g, "")
                    value = value.replace(/ /g, "")
                    const courseCategoryIds = value.split(",")
                    if (courseCategoryIds.length > 0 && courseCategoryIds[0] !== "") {
                        updatedIncommingFilters.courseCategoryIds = courseCategoryIds
                    }
                } else if (key === "dayFilter" || key === "dayfilter") {
                    // console.log("dayFilter::: ", value)

                    const day = getEnumKeyByEnumValue(DayFilter, value)

                    if (day !== null) {
                        updatedIncommingFilters.dayFilter = [DayFilter[day]]
                    } else {
                        // Ungültige Werte werden bewusst verworfen (Ist-Verhalten, siehe
                        // App.parameters.test.tsx) — die Warnung hilft Integratoren beim Einbetten.
                        warnOnce(`dayFilter:${value}`, `[KursOrganizer] Ungültiger dayFilter-Wert "${value}" wird ignoriert. Gültig ist genau EIN Wert aus: ${Object.values(DayFilter).join(", ")}`)
                    }
                } else if (key === "parentUrl" || key === "parenturl") {
                    // console.log("parentUrl::: ", value)
                    parentUrl = value
                } else if (key === "showFilterMenu" || key === "showfiltermenu") {
                    // console.log("showFilterMenu::: ", value)
                    const boolValue = value === "false" ? false : true
                    otherSettings.showFilterMenu = boolValue
                } else if (key === "listType" || key === "listtype") {
                    // Angebotstyp-Filter (courses|interest|all). Ungültige Werte → "all".
                    otherSettings.listType = parseListType(value)
                } else if (key === "restrictiveCourses" || key === "restrictivecourses") {
                    handleRestrictiveCourses(value, updatedIncommingFilters)
                } else if (key === "prerequisiteCourseId" || key === "prerequisitecourseid") {
                    // console.log("prerequisiteCourseId::: ", value)
                    // updatedIncommingFilters.prerequisiteCourseId = value
                    handlePrerequisiteCourseId(value, updatedIncommingFilters)
                } else {
                    // console.log("others::: ", key, ":", value)
                }
            } else if (typeof value === "boolean") {
                switch (key) {
                    case "showOnlyInWebActive":
                        otherSettings.showOnlyInWebActive = typeof value === "boolean" ? value : false
                        break
                    case "showFilterMenu":
                        otherSettings.showFilterMenu = typeof value === "boolean" ? value : false
                        break

                    default:
                        break
                }
            }
        })
    }

    const [currentLocation, setCurrentLocation] = useState<string>("")

    const Routes: FC = () => {
        const history = useHistory()

        useEffect(() => {
            setCurrentLocation(history.location.pathname)
        }, [history.location.pathname])

        // // Add this new useEffect to handle navigation
        // useEffect(() => {
        //     if (parameters?.navigateTo) {
        //         const targetUrl = navigationMap[parameters.navigateTo.toLowerCase()]
        //         if (targetUrl) {
        //             history.push(targetUrl)
        //         }
        //     }
        // }, [history, parameters?.navigateTo])

        useEffect(() => {
            if (internalPath === "URL_LOGIN_COMPLETE_PASSWORDLESS") {
                const courseDetailsUrl = URL_COURSE_DETAILS.replace(":id", courseId ?? "")
                history.push({ pathname: URL_LOGIN_COMPLETE_PASSWORDLESS, state: { fromUrl: courseDetailsUrl } })
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [internalPath])

        if (!appSettings) return null

        return (
            <>
                <Switch>
                    <Route exact path={URL_COURSE_DETAILS} component={CourseDetails} />
                    <Route exact path={URL_LOGIN_EMAIL} render={(props) => <LoginPanel setToken={(token) => setAccesToken(token)} email={props.match.params.email} parentUrl={parentUrl} />} />
                    <Route exact path={URL_LOGIN}>
                        {isLoggedIn ? <Redirect to={URL_ACCOUNT} /> : <LoginPanel setToken={(token) => setAccesToken(token)} parentUrl={parentUrl} />}
                    </Route>
                    <Route
                        exact
                        path={URL_LOGIN_COMPLETE_PASSWORDLESS_EMAIL}
                        render={(props) => (
                            <CompletePasswordLessFlowPanel
                                setToken={(token) => setAccesToken(token)}
                                email={props.match.params.email}
                                parentUrl={parentUrl}
                                paramCode={code}
                                passwordlessIdCodeLength={appSettings.passwordlessIdCodeLength}
                                setParentParams={(parentParams?: ParentParams) => setParentParams(parentParams)}
                            />
                        )}
                    />
                    <Route exact path={URL_LOGIN_COMPLETE_PASSWORDLESS}>
                        <CompletePasswordLessFlowPanel
                            setToken={(token) => setAccesToken(token)}
                            parentUrl={parentUrl}
                            paramCode={code}
                            passwordlessIdCodeLength={appSettings.passwordlessIdCodeLength}
                            setParentParams={(parentParams?: ParentParams) => setParentParams(parentParams)}
                        />
                    </Route>
                    <Route exact path={URL_REGISTER}>
                        <RegistrationPanel useCase='registration' companySettings={companySettings} />
                    </Route>
                    {/* Aktivierungslink aus der Bestätigungs-Mail (sc-3358) — öffentlich, kein Auth-Guard */}
                    <Route exact path={URL_ACTIVATE_ACCOUNT}>
                        <ActivateAccountPanel setToken={(token) => setAccesToken(token)} />
                    </Route>
                    <Route exact path={URL_REGISTER_SEPA_EDIT}>
                        <SepaDataEdit />
                    </Route>
                    <Route exact path={URL_REGISTER_SEPA_EDIT_ID} render={(props) => <SepaDataEdit id={props.match.params.id} /* bookerId={props.match.params.bookerId} */ />} />
                    <Route exact path={URL_FORGOT_PASSWORD}>
                        <ForgotPasswordPanel parentUrl={parentUrl} />
                    </Route>
                    <Route
                        path={URL_ACCOUNT}
                        render={({ location }) =>
                            isLoggedIn ? (
                                <AccountDashboard companySettings={companySettings} />
                            ) : (
                                // Ausgeloggt: zum Login mit Rückkehr zur angeforderten Konto-Route
                                // (etabliertes fromUrl-Muster des Interessenten-Flows, siehe LoginPanel).
                                // search mitnehmen, damit z.B. ?tab=past den Login überlebt.
                                <Redirect to={{ pathname: URL_LOGIN, state: { fromUrl: { pathname: location.pathname, search: location.search || undefined } } }} />
                            )
                        }
                    />
                    <Route exact path={URL_BOOKING_SUMMARY}>
                        <BookingSummary />
                    </Route>
                    <Route exact path={URL_RESULTS} render={(props) => <ResultScreen resultType={props.match.params.resultType} />} />
                    {/* AGB-Route verfügbar */}
                    <Route exact path={URL_AGB}>
                        <AGB />
                    </Route>
                    {/* Datenschutz-Route verfügbar */}
                    <Route exact path={URL_DATENSCHUTZ}>
                        <Datenschutz />
                    </Route>
                    {/* Widerruf-Route (öffentlich, kein Auth-Guard), aber nur erreichbar,
                        wenn der Tenant die Funktion freigeschaltet hat. Sonst zurück zur
                        Startseite — der Footer-Link ist ohnehin nur bei aktivem Flag
                        sichtbar, und das Backend nimmt Widerrufe nur dann an (kein
                        irreführender "Erfolg" bei deaktiviertem Feature). */}
                    <Route exact path={URL_WIDERRUF}>
                        {appSettings.withdrawalButtonEnabled ? <Widerruf /> : <Redirect to={URL_BASE} />}
                    </Route>
                    {/* Phase 9: Mollie Payment Return-Page nach Hosted-Checkout-Redirect */}
                    <Route path={URL_PAYMENT_RETURN} component={PaymentReturn} />
                    {/* Interessenten-Liste: öffentliche Details + login-pflichtige Anmeldung */}
                    <Route exact path={URL_INTEREST_REGISTRATION} component={InterestRegistration} />
                    <Route exact path={URL_INTEREST_DETAILS} component={InterestDetails} />
                    <Route exact path={URL_BASE}>
                        <CourseTableContainer incommingFilters={updatedIncommingFilters} otherSettings={otherSettings} appSettings={appSettings} />
                    </Route>
                    {/* Catch-all Route muss wirklich die letzte sein */}
                    <Route path={URL_BASE}>
                        <CourseTableContainer incommingFilters={updatedIncommingFilters} otherSettings={otherSettings} appSettings={appSettings} />
                    </Route>
                </Switch>
            </>
        )
    }
    return (
        <ConfigProvider locale={antdLocale}>
            <AppRouter history={embedHistory}>
                <MainLayout currentLocation={currentLocation} id='mainLayout'>
                    {!maintenanceActive && process.env.REACT_APP_ENV !== "production" && (
                        <div>
                            {Object.entries(updatedIncommingFilters).map(([filter, value]) => {
                                return <div key={filter}>{`${filter}: ${value}`}</div>
                            })}
                            {atLeastOneParentUrlParam && parentUrlParams && (
                                <Space>
                                    {Object.entries(parentUrlParams).map(([filter, value]) => {
                                        if (!value) return null
                                        return <div key={filter}>{`|${filter}: ${value} `}</div>
                                    })}
                                </Space>
                            )}
                        </div>
                    )}
                    {maintenanceActive ? (
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                minHeight: "60vh",
                                padding: "24px 16px",
                                textAlign: "center",
                            }}
                        >
                            <Alert
                                type='info'
                                showIcon={false}
                                message='Wartungsarbeiten'
                                description={
                                    <div>
                                        <div>{maintenanceInfo?.message || "Wir sind in Kürze wieder da."}</div>
                                        {maintenanceUntilText && <div>Voraussichtlich bis {maintenanceUntilText}</div>}
                                    </div>
                                }
                            />
                        </div>
                    ) : (
                        <ErrorBoundary>
                            <CourseFilterProvider seed={updatedIncommingFilters}>
                                <Routes />
                            </CourseFilterProvider>
                        </ErrorBoundary>
                    )}
                </MainLayout>
            </AppRouter>
        </ConfigProvider>
    )
}

export default App
